import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import { get_countries, get_states } from "../../../services/apis/locations";
import { create_user, edit_user } from "../../../services/apis/users";
import type {
  IUser,
  IUserCreate,
  IUserUpdate,
} from "../../../store/types/users";

type UserFormValues = Omit<IUserCreate, "country_id" | "state_id"> & {
  country_id: string;
  state_id: string;
};

type UserFormErrors = Partial<Record<keyof UserFormValues, string>>;

const emptyValues: UserFormValues = {
  email: "",
  username: "",
  password: "",
  phone_number: "",
  name: "",
  is_elite: false,
  country_id: "",
  state_id: "",
};

const useUserForm = (open: boolean, user: IUser | null) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ user: state }) => state.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const states = useAppSelector(({ locations }) => locations.states);
  const locationsLoading = useAppSelector(({ locations }) => locations.loading);
  const [values, setValues] = React.useState<UserFormValues>(emptyValues);
  const [errors, setErrors] = React.useState<UserFormErrors>({});

  React.useEffect(() => {
    if (!open) return;

    setErrors({});
    if (!user) {
      setValues(emptyValues);
      return;
    }

    setValues({
      email: user.email,
      username: user.username,
      password: "",
      phone_number: user.profile.phone,
      name: user.profile.name,
      is_elite: user.is_elite,
      country_id: String(user.profile.country_id),
      state_id: String(user.profile.state_id ?? ""),
    });
    if (user.profile.country_id) {
      void get_states(dispatch, user.profile.country_id);
    }
  }, [dispatch, open, user]);

  React.useEffect(() => {
    if (open && countries.length === 0) void get_countries(dispatch);
  }, [countries.length, dispatch, open]);

  const setFieldValue = <K extends keyof UserFormValues>(
    field: K,
    value: UserFormValues[K],
  ) => {
    setValues((previous) => ({ ...previous, [field]: value }));
    setErrors((previous) => ({ ...previous, [field]: "" }));
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (
      name === "email" ||
      name === "username" ||
      name === "password" ||
      name === "phone_number" ||
      name === "name"
    ) {
      setFieldValue(name, value);
    }
  };

  const validate = () => {
    const nextErrors: UserFormErrors = {};
    const phoneDigits = values.phone_number.replace(/\D/g, "");

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!/^[a-zA-Z0-9_.-]{3,30}$/.test(values.username.trim())) {
      nextErrors.username = "Enter a valid username (3-30 characters).";
    }
    if (!user && values.password.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters.";
    }
    if (values.name.trim().length < 2) {
      nextErrors.name = "Enter a name with at least 2 characters.";
    }
    if (
      !/^\+?[0-9\s().-]+$/.test(values.phone_number.trim()) ||
      phoneDigits.length < 7 ||
      phoneDigits.length > 15
    ) {
      nextErrors.phone_number = "Enter a valid phone number.";
    }
    if (!values.country_id) nextErrors.country_id = "Select a country.";
    if (!values.state_id) nextErrors.state_id = "Select a state.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) return false;

    const userFields: Omit<IUserCreate, "password"> = {
      email: values.email.trim(),
      username: values.username.trim(),
      phone_number: values.phone_number.trim(),
      name: values.name.trim(),
      is_elite: values.is_elite,
      country_id: Number(values.country_id),
      state_id: Number(values.state_id),
    };

    if (user) {
      const updates: IUserUpdate = {
        ...userFields,
        ...(values.password ? { password: values.password } : {}),
      };
      return edit_user(dispatch, user.id, updates);
    }
    const payload: IUserCreate = { ...userFields, password: values.password };
    return create_user(dispatch, payload);
  };

  return {
    values,
    errors,
    countries,
    states,
    loading: loading || locationsLoading,
    onChange,
    setFieldValue,
    submit,
  };
};

export default useUserForm;
